import { firstOfMonth, lastOfMonth } from '@/lib/dates'
import { useState } from 'react'
import { EmptyState, ErrorState, FilterBar, PageHeader } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useOrgAttendanceReport } from '../api'
import { ORG_GROUP_LABELS } from '../types'
import type { OrgGroupBy } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

function hours(minutes: number): string {
  return `${(minutes / 60).toFixed(1)}h`
}

/**
 * Attendance totalled by where people actually were.
 *
 * <p>Grouped through the assignment each day carries, not through the employee's current
 * one. That is the difference between a March report that still reads the same in June and
 * one that quietly moves March's hours to wherever somebody transferred in April.
 */
export function AttendanceReportPage() {
  useBreadcrumb()
  const [from, setFrom] = useState(firstOfMonth())
  const [to, setTo] = useState(lastOfMonth())
  const [groupBy, setGroupBy] = useState<OrgGroupBy>('SITE')

  const report = useOrgAttendanceReport(from, to, groupBy)
  const rows = report.data ?? []
  const totals = rows.reduce(
    (sum, row) => ({
      days: sum.days + row.days,
      worked: sum.worked + row.workedMinutes,
      overtime: sum.overtime + row.overtimeMinutes,
    }),
    { days: 0, worked: 0, overtime: 0 },
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attendance report"
        subtitle="Totals for where people were on the day, not where they are now."
        actions={
          <FilterBar>
            <Select
              aria-label="Group by"
              id="report-group"
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as OrgGroupBy)}
            >
              {(Object.keys(ORG_GROUP_LABELS) as OrgGroupBy[]).map((key) => (
                <option key={key} value={key}>
                  {ORG_GROUP_LABELS[key]}
                </option>
              ))}
            </Select>

            <DatePicker
              aria-label="From" id="report-from" value={from} onChange={(e) => setFrom(e.target.value)} />

            <DatePicker
              aria-label="To" id="report-to" value={to} onChange={(e) => setTo(e.target.value)} />
          </FilterBar>
        }
      />

      <Card>
        {report.isPending && <Skeleton className="m-4 h-40" />}
        {report.isError && <ErrorState error={report.error} onRetry={() => void report.refetch()} />}
        {report.isSuccess && rows.length === 0 && (
          <EmptyState
            title="No attendance in this range"
            description="Nothing has been processed for these dates yet."
          />
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[52rem] text-sm">
              <caption className="sr-only">
                Attendance by {ORG_GROUP_LABELS[groupBy].toLowerCase()}, {from} to {to}
              </caption>
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">{ORG_GROUP_LABELS[groupBy]}</th>
                  <th scope="col" className="p-3 text-right font-medium">People</th>
                  <th scope="col" className="p-3 text-right font-medium">Days</th>
                  <th scope="col" className="p-3 text-right font-medium">Present</th>
                  <th scope="col" className="p-3 text-right font-medium">Absent</th>
                  <th scope="col" className="p-3 text-right font-medium">On leave</th>
                  <th scope="col" className="p-3 text-right font-medium">Worked</th>
                  <th scope="col" className="p-3 text-right font-medium">Overtime</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.groupId ?? 'unassigned'}
                    className="border-b border-[var(--color-border)] last:border-0"
                  >
                    <td className="p-3">
                      {row.groupId == null ? (
                        // Shown rather than dropped. These are real hours; a total that
                        // silently excludes them does not reconcile with the timesheet
                        // sitting next to it.
                        <span className="text-[var(--color-muted)]" title="No assignment covers these days">
                          Unassigned
                        </span>
                      ) : (
                        row.groupName
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums">{row.employees}</td>
                    <td className="p-3 text-right tabular-nums">{row.days}</td>
                    <td className="p-3 text-right tabular-nums">{row.presentDays}</td>
                    <td className="p-3 text-right tabular-nums">{row.absentDays}</td>
                    <td className="p-3 text-right tabular-nums">{row.leaveDays}</td>
                    <td className="p-3 text-right tabular-nums">{hours(row.workedMinutes)}</td>
                    <td className="p-3 text-right tabular-nums">{hours(row.overtimeMinutes)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--color-border)] font-medium">
                  <td className="p-3">Total</td>
                  <td className="p-3" />
                  <td className="p-3 text-right tabular-nums">{totals.days}</td>
                  <td className="p-3" colSpan={3} />
                  <td className="p-3 text-right tabular-nums">{hours(totals.worked)}</td>
                  <td className="p-3 text-right tabular-nums">{hours(totals.overtime)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
